import React from "react";
import { connect } from "react-redux";
import { Dispatch } from "redux";
import { IAction } from "reduxsauce";

import { IStyle } from "../../Types/Style";
import { Header as H1 } from "../../components/utils/Headings";
import { SideMenu } from "../../components/utils/SideMenu/SideMenu.container";
import { SideMenuArrowContext, SideMenuCloseArrow } from "../../components/utils/SideMenu/SideMenuCloseArrow";
import Spinner from "../../components/utils/Spinner";
import SubTopbar from "../../components/utils/SubTopbar";
import TopBar from "../../components/utils/TopBar";
import * as selectors from "../../selectors/selectors";
import { IState } from "../reducer";
import { ICompleteOrganization, RequestState } from "../types/common";
import * as actions from "./actions";
import ThemeListPage from "./ThemesList.content";

export interface IThemeListStateProps {
  fetchEditableThemesStatus: RequestState;
  fullOrganization: ICompleteOrganization | null;
  isAdmin: boolean;
  isLoading: boolean;
  isOrgAdmin: boolean;
  organizationName: string;
  themes: IStyle[];
  userName: string;
}

export interface IThemeListDispatchProps {
  addNewTheme: () => void;
  deleteTheme: (styleId: string) => void;
  mount: () => void;
  unmount: () => void;
}

type ThemeListComponentProps = IThemeListStateProps & IThemeListDispatchProps;

export const ThemeListComponent: React.FC<ThemeListComponentProps> = props => {
  const { userName, organizationName } = props;
  const { isOpen } = React.useContext(SideMenuArrowContext);
  const [menuExpanded, setMenuExpanded] = React.useState(true);
  const toogleMenu = () => setMenuExpanded(!menuExpanded);
  const withPage = (children: React.ReactNode) => (
    <>
      <TopBar showToggleMenu={true} onToggleMenuClick={toogleMenu} userName={userName} />
      <SideMenuCloseArrow />
      <SideMenu
        expanded={menuExpanded}
        organizationName={organizationName}
        userName={userName}
        onClose={toogleMenu}
        isOpen={isOpen}
      />
      <SubTopbar withSidebar={true} isOpen={isOpen}>
        {children}
      </SubTopbar>
    </>
  );

  React.useEffect(() => {
    props.mount();
    return props.unmount;
  }, [props.organizationName]);

  if (props.isLoading) {
    return <Spinner data-testid="loading" />;
  }

  if (!props.isOrgAdmin || !props.fullOrganization) {
    return withPage(<div>Only organisation admins have access to this content.</div>);
  }

  return withPage(
    <>
      <H1>Themes</H1>
      {props.fetchEditableThemesStatus === RequestState.FAILED ? (
        <div>Fetching the themes failed. Please, try again.</div>
      ) : (
        <ThemeListPage
          organization={props.fullOrganization}
          addNewTheme={props.addNewTheme}
          themes={props.themes}
          deleteTheme={props.deleteTheme}
        />
      )}
    </>
  );
};

const mapStateToProps = (state: IState): IThemeListStateProps => ({
  fetchEditableThemesStatus: state.App.ThemeList.fetchEditableThemesStatus,
  fullOrganization: state.App.ThemeList.fullOrganization,
  isAdmin: selectors.isAdminUser(state),
  isLoading:
    state.App.ThemeList.addNewThemeStatus === RequestState.PENDING ||
    state.App.ThemeList.fetchEditableThemesStatus === RequestState.PENDING ||
    state.App.ThemeList.deleteThemeStatus === RequestState.PENDING,
  isOrgAdmin: selectors.isOrgAdmin(state),
  organizationName: selectors.organizationName(state),
  themes: state.App.ThemeList.themes,
  userName: selectors.accountName(state),
});

const mapDispatchToProps = (dispatch: Dispatch<IAction>): IThemeListDispatchProps => ({
  addNewTheme: () => dispatch(actions.addNewTheme()),
  deleteTheme: (styleId: string) => dispatch(actions.deleteTheme(styleId)),
  mount: () => dispatch(actions.mount()),
  unmount: () => dispatch(actions.unmount()),
});

export const ThemeList = connect(mapStateToProps, mapDispatchToProps)(ThemeListComponent);
